// الشاشة التي تُفتح ٩٠٪ من الوقت: مبلغ + وجهة + نوع اختياري.
// لا مُبدّل أوضاع: المستخدم يتوقف حيث يشاء — «تم» تعمل بمجرد وجود مبلغ ووجهة.

import { fmt, money, esc, monthStart, MONTHS } from '../util.js';
import { addEntry, addDest, addType, baseCur, baseSym, CURRENCIES, stats,
         netTotal, netScope, toggleNetScope } from '../store.js';
import { autoDestFor } from '../model.js';
import { calcBox, sentence, destStrip, typeStrip, keypad, topBar, askName } from '../ui.js';
import * as calc from '../calc.js';

// العملة واحدة تُختار عند فتح الحساب، فلا حقل عملة في كل حركة
export function freshDraft(){
  return { calc: calc.create(), destId: null, typeId: null, auto: false, kind: 'out',
           note: '', noteOpen: false };
}

/**
 * رقمان دائمان تحت العنوان: مصروف الشهر، والصافي بجانبه.
 * والصافي وحده يحمل مداه ويتبدّل بالضغط — ثلاثةُ أرقامٍ في سطرٍ واحدٍ ازدحام،
 * وإخفاؤه خلف ضغطةٍ يُفقده فائدته. فالاسمُ يتغيّر مع المدى فلا يلتبسان.
 */
function headerStats(monthTotal){
  const scope = netScope();
  const net = netTotal(scope);
  const sym = baseSym();
  const sign = net < 0 ? '‎− ' : '+ ';
  return `<span class="hstat">الشهر <b class="mono">${fmt(monthTotal)}</b> ${sym}</span>
    <span class="hsep">·</span>
    <button type="button" class="hstat netbtn" data-act="net-scope">
      ${scope === 'all' ? 'الصافي الكلي' : 'صافي الشهر'}
      <b class="mono ${net < 0 ? 'neg' : 'pos'}">${sign}${fmt(Math.abs(net))}</b> ${sym}
      <span class="caret" aria-hidden="true">▾</span>
    </button>`;
}

/**
 * الملاحظة اختياريةٌ كالنوع: زرٌّ متقطّعٌ لا حقلٌ مفتوح.
 * الحركة الكاملة تبقى رقماً وضغطةَ وجهٍ و«تم»، ومن لا يريد ملاحظةً لا يرى
 * إلّا سطراً لا يعترض طريقه — ولا تمنع الحفظ ولا تُطلب أبداً.
 */
export function noteField(d){
  if (!d.noteOpen && !d.note){
    return `<button type="button" class="notebtn" data-act="note-open">
        ＋ ملاحظة <em>اختياري</em></button>`;
  }
  const len = (d.note || '').length;
  return `<div class="notefield">
    <textarea id="entryNote" rows="2" maxlength="500"
      placeholder="سوبرماركت الأمل — تموين الأسبوع">${esc(d.note || '')}</textarea>
    <div class="noterow">
      <span class="cnt">${len} / 500</span>
      <button type="button" data-act="note-clear">إزالة الملاحظة</button>
    </div>
  </div>`;
}

export function render(app){
  const d = app.draft;
  const income = d.kind === 'in';
  // من العدّادات لا من مسح التاريخ: التاريخ ليس على الجهاز
  const pending = stats().untypedCount || 0;

  const now = Date.now();
  const monthTotal = stats().monthTotal || 0;

  const caption = (d.destId || d.typeId)
    ? sentence(d.destId, d.typeId)
    : '<span class="ph">اكتب المبلغ واختر الوجهة — بأي ترتيب</span>';

  // مبدّل مصروف/دخل صغير في الشريط العلوي: يُستخدم مرة في الشهر،
  // فلا يستحق شريطاً كاملاً يسرق ارتفاعاً من أشرطة البُعدين.
  const kindToggle = `<div class="kindtog">
      <button type="button" data-kind="out" aria-pressed="${!income}">مصروف</button>
      <button type="button" data-kind="in" aria-pressed="${income}">دخل</button>
    </div>`;

  // «تم» يحسب ويحفظ في خطوة واحدة — لا زر «=» منفصل يضيف ضغطة على كل حركة
  const total = calc.value(d.calc);
  const ready = total !== null && total > 0 && d.destId;

  return topBar(income ? 'دخل جديد' : 'مصروف جديد', headerStats(monthTotal), kindToggle)
    + calcBox(d.calc, baseCur(), caption, income)
    + '<div class="midscroll">'
    +   destStrip(d.destId, { auto: d.auto })
    +   (income ? '' : typeStrip(d.typeId, {}))
    + '</div>'
    + keypad()
    + noteField(d)
    + `<button type="button" class="cta${income ? ' income' : ''}" data-act="save" ${ready ? '' : 'disabled'}>
        ${income ? 'تسجيل الدخل' : 'تم'}
       </button>`
    + (pending && !income
        ? `<button type="button" class="nudge" data-act="classify">صنّف لاحقاً<em>${pending} حركة بلا نوع</em></button>`
        : '')
    + `<div class="linkrow">
         <button type="button" data-act="day">يوم كامل</button>
         <button type="button" data-act="split">توزيع</button>
         <button type="button" data-act="text">سطر نصي</button>
       </div>`;
}

/**
 * الكتابة في الملاحظة لا تُعيد الرسم: إعادةُ الرسم تُفقد التركيزَ ومكانَ المؤشّر
 * بعد كلّ حرف. نقرأ القيمة في المسودّة ونحدّث العدّاد بيدنا.
 */
export function mount(app, root){
  const box = root.querySelector('#entryNote');
  if (!box) return;
  const cnt = root.querySelector('.notefield .cnt');
  if (app.draft.noteOpen && !box.value) box.focus();
  box.oninput = () => {
    app.draft.note = box.value;
    if (cnt) cnt.textContent = `${box.value.length} / 500`;
  };
}

export function onClick(app, btn){
  const d = app.draft;

  if (btn.dataset.act === 'net-scope'){ toggleNetScope(); return app.render(); }
  if (btn.dataset.act === 'note-open'){ d.noteOpen = true; return app.render(); }
  if (btn.dataset.act === 'note-clear'){ d.note = ''; d.noteOpen = false; return app.render(); }

  if (btn.dataset.key){ d.calc = calc.press(d.calc, btn.dataset.key); return app.render(); }
  if (btn.dataset.kind){
    d.kind = btn.dataset.kind;
    if (d.kind === 'in') d.typeId = null;
    return app.render();
  }
  if (btn.dataset.dest){
    d.destId = d.destId === btn.dataset.dest ? null : btn.dataset.dest;
    d.auto = false;
    return app.render();
  }

  if (btn.dataset.type){
    const id = btn.dataset.type;
    if (d.typeId === id){ d.typeId = null; return app.render(); }
    d.typeId = id;
    // الاقتران المتعلَّم: الوجهة تُختار وحدها ما لم يكن المستخدم قد اختارها يدوياً
    if (!d.destId || d.auto){
      const guess = autoDestFor(id);
      if (guess){ d.destId = guess; d.auto = true; }
    }
    return app.render();
  }

  if (btn.dataset.add === 'dest'){
    const name = askName('اسم الوجهة الجديدة (لمن أو لأين):');
    if (name){ const x = addDest(name); if (x){ d.destId = x.id; d.auto = false; } }
    return app.render();
  }
  if (btn.dataset.add === 'type'){
    const name = askName('اسم النوع الجديد (على ماذا):');
    if (name){ const x = addType(name); if (x) d.typeId = x.id; }
    return app.render();
  }

  const act = btn.dataset.act;
  if (act === 'calc'){
    return app.openCalc(calc.value(d.calc), v => {
      d.calc = calc.fromNumber(v);
      app.render();
    });
  }
  if (act === 'save') return save(app);
  if (act === 'day')      return app.openSub('day');
  if (act === 'split')    return app.openSub('split');
  if (act === 'text')     return app.openSub('text');
  if (act === 'classify') return app.openSub('classify');
  return false;
}

function save(app){
  const d = app.draft;
  const amount = calc.value(d.calc);
  if (amount === null || amount <= 0 || !d.destId) return;
  const e = addEntry({
    amount, destId: d.destId,
    typeId: d.kind === 'in' ? null : d.typeId,
    currency: baseCur(), kind: d.kind,
    // مسافاتٌ وحدها ليست ملاحظة
    note: (d.note || '').trim()
  });
  const sym = (CURRENCIES[e.currency] || { sym: '' }).sym;
  const label = `${d.kind === 'in' ? 'دخل' : 'تم'} · ${money(e.amount)} ${sym}`;
  app.draft = freshDraft();
  app.render();
  app.toast(label, 'تراجع', () => app.undoEntries([e.id]));
  app.queueSync();
}
